import React, { useState, useEffect, useRef } from 'react';
import './Hero.css';
import { Link } from 'react-router-dom';

const Hero = () => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Sample images for the slider - you can replace these with your actual images
  const sliderImages = [
    { name: 'Australia', image: '/Australia.jpg', url: '/country/australia' },
    { name: 'Canada', image: '/Canada.jpg', url: '/country/canada' },
    { name: 'Denmark', image: '/Denmark.jpg', url: '/country/denmark' },
    { name: 'Finland', image: '/Finland.avif', url: '/country/finland' },
    { name: 'Ireland', image: '/Ireland.jpg', url: '/country/ireland' },
    { name: 'New York', image: '/New_York.avif', url: '/country/new-york' },
    { name: 'New Zeeland', image: '/New_Zeeland.jpg', url: '/country/new-zealand' },
    { name: 'Sweden', image: '/Swden.jpg', url: '/country/sweden' },
    { name: 'United Kingdom', image: '/UK.jpg', url: '/country/uk' },
  ];
  const [currentSlide, setCurrentSlide] = useState(sliderImages.length);

  // Create infinite list by duplicating images
  const infiniteImages = [...sliderImages, ...sliderImages, ...sliderImages];

  // Function to clear the current timer
  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setTimerActive(false);
      // console.log('Timer cleared'); // Debug log
    }
  };

  // Function to start the timer
  const startTimer = () => {
    // Clear any existing timer first
    clearTimer();
    
    // console.log('Starting new timer...'); // Debug log
    setTimerActive(true);
    
    // Start new timer
    intervalRef.current = setInterval(() => {
      // console.log('Timer triggered - auto advancing slide'); // Debug log
      setCurrentSlide((prev) => {
        const nextSlide = prev + 1;
        setIsTransitioning(true);
        // If we're going to a duplicate slide (beyond original images)
        if (nextSlide >= sliderImages.length*2) {
          setIsTransitioning(true);
          
          // After a short delay, reset to slide 0 without transition
          setTimeout(() => {
            setCurrentSlide(sliderImages.length);
            setIsTransitioning(false);
          }, 1000); // Wait for the 1-second transition to complete
          
          return nextSlide; // Show the duplicate slide first
        }
        
        return nextSlide;
      });
    }, 5000); // Change slide every 5 seconds
  };

  // Function to restart the timer
  const restartTimer = () => {
    clearTimer();
    startTimer();
  };

  // Test function to manually trigger timer restart
  const testTimerRestart = () => {
    restartTimer();
  };

  useEffect(() => {
    // Start the timer on component mount
    startTimer();

    // Cleanup on unmount
    return () => {
      clearTimer();
    };
  }, []); // Empty dependency array to run only once

  const goToSlide = (index) => {
    setCurrentSlide(sliderImages.length + index);
    restartTimer();
  };

  const goToNext = () => {
    setCurrentSlide((prev) => {
      const nextSlide = prev + 1;
      setIsTransitioning(true);
      // If we're going to a duplicate slide
      if (nextSlide >= sliderImages.length*2) {
        setIsTransitioning(true);
        
        // After transition completes, reset to slide 0
        setTimeout(() => {
          setCurrentSlide(sliderImages.length);
          setIsTransitioning(false);
        }, 1000);
        
        return nextSlide;
      }
      
      return nextSlide;
    });
    restartTimer();
  };

  const goToPrev = () => {
    setCurrentSlide((prev) => {
      const prevSlide = prev - 1;
      setIsTransitioning(true);
      // If we're going to a duplicate slide
      if (prevSlide < sliderImages.length) {
        setIsTransitioning(true);
        
        // After transition completes, reset to slide 0
        setTimeout(() => {
          setCurrentSlide((sliderImages.length*2) - 1);
          setIsTransitioning(false);
        }, 1000);
        
        return prevSlide;
      }
      
      return prevSlide;
    });
    restartTimer();
  };

  // Calculate which dot should be active based on current slide
  const getActiveDotIndex = () => {
    return currentSlide % sliderImages.length;
  };

  // Calculate transform value
  const getTransformValue = () => {
    return currentSlide * (100 / infiniteImages.length);
  };

  return (
    <section className="hero">
      <div className="container" style={{maxWidth: '100%'}}>
        <div className="hero-content">
          <div className="hero-slider-container">
            <div className="hero-slider" ref={sliderRef}>
              <div 
                className="slider-track"
                style={{
                  transform: `translateX(-${getTransformValue()}%)`,
                  width: `${infiniteImages.length * 100}%`,
                  transition: isTransitioning ? 'transform 1.0s ease-in-out' : 'none'
                }}
              >
                  {infiniteImages.map((country, index) => (
                   <div key={index} className="slide" style={{
                     backgroundImage: `url(${country.image})`, 
                     backgroundSize: 'cover', 
                     backgroundPosition: 'center', 
                     backgroundRepeat: 'no-repeat',
                     width: `${100 / infiniteImages.length}%`
                   }}>
                     <Link to={country.url}>
                      <div className="slide-overlay">
                        <h3>Study in {country.name}</h3>
                        <p>Click to Learn More</p>
                      </div>
                     </Link>
                   </div>
                 ))}
              </div>
            </div>
            
            <div className="slider-navigation">
              <button className="slider-nav-btn prev" onClick={goToPrev}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="slider-nav-btn next" onClick={goToNext}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
            
            <div className="slider-dots">
              {sliderImages.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === getActiveDotIndex() ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
